import { FC, ReactNode } from 'react';
import dynamic from 'next/dynamic';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { useFormat } from 'helpers/hooks/useFormat';
import Link from '../../../../atoms/link';
import { UIProductContent } from '../types';
import { AdditionalSection } from './additional-section';

const RichText = dynamic(() => import('../../../../atoms/rich-text'));
const InfoItem = dynamic(() => import('../../../../atoms/info-item'));

interface AdditionalInfoProps extends Omit<UIProductContent, 'description'> {
  description?: ReactNode;
}

const AdditionalInfo: FC<AdditionalInfoProps> = ({
  additionalDetails,
  description,
  frequentlyAskedQuestions,
  productGuides,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });

  return (
    <>
      <div className="col-span-8 mt-24 md:col-span-7 md:mt-41">
        {description && (
          <AccordionBtn
            className="mt-24 border-y-2 border-neutral-400 md:mt-0"
            closedSectionTitle={formatMessage({ id: 'product.desc', defaultMessage: 'Product Description' })}
            buttonClassName="py-16 px-8 font-medium text-16 leading-loose tracking-normal text-text-primary"
            panelClassName="pb-12 px-8"
          >
            {description}
          </AccordionBtn>
        )}
        {additionalDetails?.length && (
          <AccordionBtn
            className="border-b-2 border-neutral-400"
            closedSectionTitle={formatMessage({ id: 'details.additional', defaultMessage: 'Additional details' })}
            buttonClassName="py-16 px-8 font-medium text-16 leading-loose tracking-normal text-text-primary"
            panelClassName="pb-12 px-8"
          >
            {additionalDetails.map((item) => (
              <InfoItem key={item.id} text={item.text} icon={item.icon} />
            ))}
          </AccordionBtn>
        )}
      </div>
      <div className="col-span-12 mt-32 grid lg:grid-cols-2 lg:gap-64">
        {frequentlyAskedQuestions?.length && (
          <AdditionalSection title={formatMessage({ id: 'faq', defaultMessage: 'Frequently Asked Questions' })}>
            {frequentlyAskedQuestions.map((faq) => (
              <AccordionBtn
                key={faq.id}
                closedSectionTitle={faq.question}
                // TODO: Move these classes to the accordion component to unify usage
                className="border-b-2 border-neutral-400"
                buttonClassName="py-16 px-8 font-medium text-16 leading-loose tracking-normal text-text-primary"
                panelClassName="pb-12 px-8"
                variant="plusAndMinus"
              >
                <RichText content={faq.answer} variant="small" className="text-text-tertiary" />
              </AccordionBtn>
            ))}
          </AdditionalSection>
        )}
        {!!productGuides?.length && (
          <AdditionalSection
            className="flex flex-col gap-12"
            title={formatMessage({ id: 'product.guides', defaultMessage: 'Product Guides' })}
          >
            {productGuides.map((link) => (
              <Link variant="primary" key={link.id} link={link.reference} target="_blank">
                {link.title}
              </Link>
            ))}
          </AdditionalSection>
        )}
      </div>
    </>
  );
};

export default AdditionalInfo;
